import type {
  AutomationView,
  TileColumns,
  UiPreferencesResponse,
  UiTheme,
  UpdateUiPreferencesDto,
} from '@adweb/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

export const UI_PREFERENCES_KEY = ['ui-preferences'] as const;

// Mêmes valeurs que DEFAULT_UI_THEME / DEFAULT_AUTOMATION_VIEW de @adweb/shared.
// Le front n'importe que des types de ce paquet (build CommonJS, valeurs non
// résolues par Vite) ; le serveur reste la source de vérité via /auth/me/preferences.
const DEFAULT_UI_THEME: UiTheme = 'MARINE';
const DEFAULT_AUTOMATION_VIEW: AutomationView = 'LIST';
const DEFAULT_TILE_COLUMNS: TileColumns = 3;

export const TILE_COLUMN_OPTIONS: TileColumns[] = [3, 4, 5];

// Dernier style appliqué, mémorisé dans le navigateur pour éviter un flash de
// l'autre style au rechargement (simple confort d'affichage, aucune donnée sensible).
const THEME_STORAGE_KEY = 'adweb.uiTheme';

export const THEME_OPTIONS: Array<{
  value: UiTheme;
  label: string;
  description: string;
  swatch: [string, string, string];
}> = [
  {
    value: 'MARINE',
    label: 'Marine',
    description: 'Barre latérale bleu nuit, contenu contrasté. Style par défaut.',
    swatch: ['#0B1B3A', '#EEF2F7', '#F26522'],
  },
  {
    value: 'CLARTE',
    label: 'Clarté',
    description: 'Interface claire et sobre, la couleur réservée aux actions.',
    swatch: ['#FFFFFF', '#F6F8FB', '#257CFF'],
  },
  {
    value: 'CONTINUITE',
    label: 'Continuité',
    description: 'Le style historique Adweb : dégradé bleu et accents orange.',
    swatch: ['#257CFF', '#FBF7F1', '#F26522'],
  },
  {
    value: 'SOMBRE',
    label: 'Sombre',
    description: 'Fonds foncés et textes clairs, plus reposant en faible luminosité.',
    swatch: ['#070C18', '#121A2B', '#257CFF'],
  },
];

export const VIEW_OPTIONS: Array<{ value: AutomationView; label: string; description: string }> = [
  {
    value: 'LIST',
    label: 'Liste',
    description: 'Un tableau compact avec un bouton d’action par automatisation.',
  },
  {
    value: 'TILES',
    label: 'Tuiles',
    description: 'Une carte par automatisation avec un grand bouton pour la lancer.',
  },
];

export function themeLabel(value: UiTheme | null | undefined): string {
  return THEME_OPTIONS.find((o) => o.value === value)?.label ?? '—';
}
export function viewLabel(value: AutomationView | null | undefined): string {
  return VIEW_OPTIONS.find((o) => o.value === value)?.label ?? '—';
}

function readStoredTheme(): UiTheme {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (v && THEME_OPTIONS.some((o) => o.value === v)) return v as UiTheme;
  } catch {
    // Stockage indisponible (navigation privée…) : on garde le défaut.
  }
  return DEFAULT_UI_THEME;
}

/** Préférences d'affichage effectives de l'utilisateur connecté. */
export function useUiPreferences() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: [...UI_PREFERENCES_KEY, user?.id],
    queryFn: async () => (await apiClient.get<UiPreferencesResponse>('/auth/me/preferences')).data,
    enabled: !!user,
    staleTime: 5 * 60_000,
  });
  const update = useMutation({
    mutationFn: async (dto: UpdateUiPreferencesDto) =>
      (await apiClient.patch<UiPreferencesResponse>('/auth/me/preferences', dto)).data,
    onSuccess: (data) => qc.setQueryData([...UI_PREFERENCES_KEY, user?.id], data),
  });
  return {
    data: query.data,
    uiTheme: query.data?.uiTheme ?? readStoredTheme(),
    automationView: query.data?.automationView ?? DEFAULT_AUTOMATION_VIEW,
    tileColumns: query.data?.tileColumns ?? DEFAULT_TILE_COLUMNS,
    hideQuickAccess: query.data?.hideQuickAccess ?? false,
    sidebarCollapsed: query.data?.sidebarCollapsed ?? false,
    update,
  };
}

/** Applique le style effectif sur <html data-ui-theme>. À monter une fois dans le shell. */
export function UiThemeEffect() {
  const { uiTheme } = useUiPreferences();
  useEffect(() => {
    document.documentElement.dataset.uiTheme = uiTheme;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, uiTheme);
    } catch {
      // Ignoré : le style reste appliqué pour la session.
    }
  }, [uiTheme]);
  // Hors de l'espace connecté (déconnexion → page de connexion), les pages
  // publiques gardent leur apparence claire d'origine.
  useEffect(
    () => () => {
      delete document.documentElement.dataset.uiTheme;
    },
    [],
  );
  return null;
}
